import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { plannerAPI, citiesAPI } from '../services/api';
import AffiliateCTA from '../components/AffiliateCTA';
import SEOHead from '../components/SEOHead';
import {
  MapPin,
  Ticket,
  Utensils,
  Trophy,
  Lightbulb,
  Sparkles,
  Clock,
  Share2,
  Download,
} from 'lucide-react';

const INTERESTS = ['food', 'nature', 'nightlife', 'landmarks', 'museums', 'sightseeing', 'football culture'];

const ALLOWED_IMAGE_HOSTS = ['unsplash.com', 'images.unsplash.com', 'cdn.unsplash.com'];

function isSafeImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return false;
    return ALLOWED_IMAGE_HOSTS.some((h) => u.hostname === h || u.hostname.endsWith('.' + h));
  } catch {
    return false;
  }
}

function buildMapUrl(placeName, city) {
  const q = [placeName, city].filter(Boolean).join(' ');
  if (!q) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function isSafeGoogleMapsUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = url.trim();
    return u.startsWith('https://www.google.com/maps') && !u.includes('goo.gl') && !u.includes('maps.app.goo.gl');
  } catch {
    return false;
  }
}

function ActivityCard({ activity, city }) {
  const builtMapUrl = activity.placeName ? buildMapUrl(activity.placeName, city) : null;
  const mapUrl = isSafeGoogleMapsUrl(activity.mapLink) ? activity.mapLink : builtMapUrl;
  const showImage = isSafeImageUrl(activity.imageUrl);

  return (
    <div className="flex flex-col sm:flex-row gap-3 p-4 rounded-xl bg-surface-container-lowest dark:bg-slate-800/80 border border-outline-variant/10 dark:border-slate-700">
      <div className="sm:w-32 flex-shrink-0">
        {showImage ? (
          <img
            src={activity.imageUrl}
            alt=""
            className="w-full h-24 object-cover rounded-lg"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div
          className={`w-full h-24 rounded-lg bg-surface-container-low dark:bg-slate-700 flex items-center justify-center ${showImage ? 'hidden' : ''}`}
          aria-hidden="true"
        >
          {mapUrl ? (
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-0.5 text-on-surface-variant hover:text-primary w-full h-full rounded-lg"
            >
              <MapPin className="h-8 w-8" />
              <span className="text-xs">Map</span>
            </a>
          ) : (
            <span className="text-3xl text-outline">📍</span>
          )}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-on-surface dark:text-white">
          {activity.suggested_time && (
            <span className="text-secondary font-mono mr-2 text-sm">{activity.suggested_time}</span>
          )}
          {activity.name}
        </h4>
        {activity.description && <p className="text-sm text-on-surface-variant dark:text-slate-400 mt-1">{activity.description}</p>}
        <div className="flex flex-wrap gap-2 mt-2">
          {mapUrl && (
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary font-medium hover:underline"
            >
              <MapPin className="h-4 w-4" />
              View on map
            </a>
          )}
          {activity.ticketLink && activity.ticketLink.startsWith('https') && (
            <a
              href={activity.ticketLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary font-medium hover:underline"
            >
              <Ticket className="h-4 w-4" />
              Get tickets
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function SlotSection({ title, activities, city }) {
  if (!activities?.length) return null;
  return (
    <div>
      <h4 className="text-xs font-bold text-outline dark:text-slate-500 uppercase tracking-wider mb-2">{title}</h4>
      <div className="space-y-2">
        {activities.map((a, i) => (
          <ActivityCard key={i} activity={typeof a === 'string' ? { name: a } : a} city={city} />
        ))}
      </div>
    </div>
  );
}

const DURATION_OPTIONS = Array.from({ length: 14 }, (_, i) => i + 1);

const ItineraryPlanner = () => {
  const [searchParams] = useSearchParams();
  const [city, setCity] = useState('');
  const [days, setDays] = useState(3);
  const [matchDay, setMatchDay] = useState('');
  const [budget, setBudget] = useState('medium');
  const [interests, setInterests] = useState([]);
  const [groupType, setGroupType] = useState('');
  const [transport, setTransport] = useState('');
  const [premium, setPremium] = useState(false);

  const { data: cities = [] } = useQuery({ queryKey: ['cities'], queryFn: () => citiesAPI.getAll().then((r) => r.data) });
  const mutation = useMutation({ mutationFn: (data) => plannerAPI.itinerary(data).then((r) => r.data) });

  const cityIsOnList = city && cities.some((c) => c.name === city);

  useEffect(() => {
    const cityParam = searchParams.get('city');
    if (cityParam !== null && cityParam !== '') {
      const trimmed = cityParam.trim();
      if (cities.length) {
        const match = cities.find((c) => c.name && c.name.toLowerCase() === trimmed.toLowerCase());
        setCity(match ? match.name : trimmed);
      } else {
        setCity(trimmed);
      }
    }
    const daysParam = searchParams.get('days');
    if (daysParam) {
      const d = parseInt(daysParam, 10);
      if (!Number.isNaN(d) && d >= 1 && d <= 14) setDays(d);
    }
    const md = searchParams.get('matchDay');
    if (md !== null && md !== '') {
      const n = parseInt(md, 10);
      if (!Number.isNaN(n) && n >= 1 && n <= 14) setMatchDay(String(n));
    }
    const bud = searchParams.get('budget');
    if (bud === 'low' || bud === 'medium' || bud === 'high') setBudget(bud);
    const tr = searchParams.get('transport');
    if (tr === 'public_transport' || tr === 'rental_car') setTransport(tr);
    if (searchParams.get('premium') === '1' || searchParams.get('premium') === 'true') setPremium(true);
  }, [searchParams, cities]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const effectiveCity = city || cities[0]?.name;
    mutation.mutate({
      city: effectiveCity,
      days,
      matchDay: matchDay ? parseInt(matchDay, 10) : null,
      budget,
      interests,
      group_type: groupType || undefined,
      transport: transport || undefined,
      premium: premium || undefined,
      city_unchosen: !city,
    });
  };

  const toggleInterest = (i) => {
    setInterests((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));
  };

  const result = mutation.data;
  const hasNewShape = result && Array.isArray(result.itinerary);
  const itineraryCity = result?.city || city || cities[0]?.name;

  const budgetBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setBudget(key)}
      className={`flex-1 py-3 rounded-xl font-bold text-sm transition-colors ${
        budget === key ? 'bg-primary text-on-primary' : 'bg-surface-container-low dark:bg-slate-800 hover:bg-primary-container/40 text-on-surface dark:text-slate-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <>
      <SEOHead
        title="AI Itinerary Planner - World Cup 2026 - WorldCup Fan Planner"
        description="Build a personalized day-by-day itinerary for host cities: food, landmarks, fan zones, and match-day tips for 2026."
      />
      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 font-body">
        <section className="mb-14 grid lg:grid-cols-2 gap-10 lg:gap-12 items-start">
          <div>
            <h1 className="font-headline text-4xl sm:text-6xl md:text-7xl font-bold tracking-tight text-primary dark:text-primary-container leading-none mb-6">
              YOUR JOURNEY <br />
              <span className="text-secondary italic">DEFINED BY AI</span>
            </h1>
            <p className="text-lg sm:text-xl text-on-surface-variant dark:text-slate-400 max-w-lg mb-8 leading-relaxed">
              Don&apos;t just watch the game—experience the culture. We build day-by-day plans from your host city, budget, and interests.
            </p>
            <div className="flex flex-wrap gap-3">
              <div className="bg-surface-container-high dark:bg-slate-800 px-4 py-2 rounded-full flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-tertiary" aria-hidden />
                <span className="text-sm font-bold text-on-surface dark:text-slate-200">16 HOST CITIES</span>
              </div>
              <div className="bg-surface-container-high dark:bg-slate-800 px-4 py-2 rounded-full flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-secondary" aria-hidden />
                <span className="text-sm font-bold text-on-surface dark:text-slate-200">3 HOST NATIONS</span>
              </div>
            </div>
          </div>

          <div className="bg-surface-container-lowest dark:bg-slate-800/70 p-6 sm:p-8 rounded-4xl shadow-fanhub relative overflow-hidden border border-outline-variant/10 dark:border-slate-700">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16" aria-hidden />
            <h2 className="font-headline text-2xl font-bold text-on-surface dark:text-white mb-6 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-primary" aria-hidden />
              Build My Itinerary
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-outline uppercase tracking-wider">Host City</label>
                  <select
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full bg-surface-container-low dark:bg-slate-900 border-none rounded-xl py-3.5 px-4 focus:ring-0 focus:bg-surface-container-lowest dark:focus:bg-slate-950 focus:border-b-2 focus:border-primary transition-all text-on-surface dark:text-slate-100"
                  >
                    <option value="">Select city</option>
                    {city && !cityIsOnList ? (
                      <option value={city}>
                        {city} (from link)
                      </option>
                    ) : null}
                    {cities.map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {city && !cityIsOnList ? (
                    <p className="text-xs text-on-surface-variant">
                      This venue uses the stadium municipality for match-day routing. You can still change duration and interests below.
                    </p>
                  ) : null}
                  {!city && cities[0]?.name && (
                    <p className="text-xs text-on-surface-variant">Defaults to {cities[0].name} if unchanged.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-outline uppercase tracking-wider">Duration</label>
                  <select
                    value={days}
                    onChange={(e) => setDays(parseInt(e.target.value, 10))}
                    className="w-full bg-surface-container-low dark:bg-slate-900 border-none rounded-xl py-3.5 px-4 focus:ring-0 text-on-surface dark:text-slate-100"
                  >
                    {DURATION_OPTIONS.map((d) => (
                      <option key={d} value={d}>
                        {d} {d === 1 ? 'Day' : 'Days'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-outline uppercase tracking-wider">Budget Level</label>
                  <div className="flex gap-2">{budgetBtn('low', 'Low')}{budgetBtn('medium', 'Med')}{budgetBtn('high', 'High')}</div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-outline uppercase tracking-wider">Travel Group</label>
                  <select
                    value={groupType}
                    onChange={(e) => setGroupType(e.target.value)}
                    className="w-full bg-surface-container-low dark:bg-slate-900 border-none rounded-xl py-3.5 px-4 focus:ring-0 text-on-surface dark:text-slate-100"
                  >
                    <option value="">Any</option>
                    <option value="solo">Solo Explorer</option>
                    <option value="couple">Couple</option>
                    <option value="family">Family (Kids)</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-outline uppercase tracking-wider">Match day (optional)</label>
                <input
                  type="number"
                  min={1}
                  max={days}
                  value={matchDay}
                  onChange={(e) => setMatchDay(e.target.value)}
                  placeholder="e.g. 2"
                  className="w-full bg-surface-container-low dark:bg-slate-900 border-none rounded-xl py-3.5 px-4 focus:ring-1 focus:ring-primary text-on-surface dark:text-slate-100"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-outline uppercase tracking-wider">Transport (optional)</label>
                <select
                  value={transport}
                  onChange={(e) => setTransport(e.target.value)}
                  className="w-full bg-surface-container-low dark:bg-slate-900 border-none rounded-xl py-3.5 px-4 focus:ring-0 text-on-surface dark:text-slate-100"
                >
                  <option value="">Any</option>
                  <option value="public_transport">Public transport</option>
                  <option value="rental_car">Rental car</option>
                </select>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="premium"
                  checked={premium}
                  onChange={(e) => setPremium(e.target.checked)}
                  className="mt-1 rounded border-outline text-primary focus:ring-primary"
                />
                <label htmlFor="premium" className="text-sm text-on-surface-variant dark:text-slate-400">
                  Detailed itinerary (premium model for richer recommendations)
                </label>
              </div>

              <div className="space-y-3">
                <label className="text-xs font-bold text-outline uppercase tracking-wider">Main Interests</label>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleInterest(i)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                        interests.includes(i)
                          ? 'bg-primary text-on-primary border-primary'
                          : 'border-outline/20 dark:border-slate-600 text-on-surface dark:text-slate-200 hover:bg-secondary-container/30'
                      }`}
                    >
                      {i.replace(/\b\w/g, (c) => c.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={mutation.isPending || (!city && cities.length === 0)}
                className="kinetic-gradient w-full py-4 sm:py-5 rounded-xl text-on-primary font-bold text-base sm:text-lg shadow-lg flex items-center justify-center gap-3 hover:brightness-110 active:scale-[0.99] transition-all disabled:opacity-50"
              >
                {mutation.isPending ? 'Generating…' : 'Generate Stadium Journey'}
                <Sparkles className="h-5 w-5" aria-hidden />
              </button>
            </form>
          </div>
        </section>

        {hasNewShape && (
          <section className="mt-8 space-y-10" aria-label="Your itinerary results">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
              <div>
                <span className="text-tertiary font-bold tracking-[0.2em] uppercase text-xs sm:text-sm">Your Custom Result</span>
                <h2 className="font-headline text-3xl sm:text-4xl font-bold text-primary dark:text-primary-container mt-2">
                  {itineraryCity ? `${itineraryCity.toUpperCase()} EXPERIENCE` : 'YOUR ITINERARY'}
                </h2>
                <p className="text-on-surface-variant dark:text-slate-400 mt-1">
                  {result.total_days} {result.total_days === 1 ? 'day' : 'days'} · AI-generated plan
                </p>
              </div>
              <div className="hidden md:flex gap-3">
                <button
                  type="button"
                  className="p-3 rounded-full bg-surface-container dark:bg-slate-800 border border-outline-variant/15 text-primary"
                  aria-label="Share"
                >
                  <Share2 className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  className="p-3 rounded-full bg-surface-container dark:bg-slate-800 border border-outline-variant/15 text-primary"
                  aria-label="Download"
                >
                  <Download className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">
              {result.itinerary.map((day, idx) => (
                <article
                  key={day.day}
                  className={`relative rounded-3xl overflow-hidden bg-surface-container-lowest dark:bg-slate-800/80 border border-outline-variant/10 dark:border-slate-700 flex flex-col border-t-4 ${
                    idx % 3 === 0 ? 'border-tertiary' : idx % 3 === 1 ? 'border-secondary' : 'border-primary'
                  } shadow-sm`}
                >
                  <div className="absolute top-4 left-4 w-11 h-11 bg-tertiary text-white flex items-center justify-center font-black text-lg z-10 rounded-lg">
                    {String(day.day).padStart(2, '0')}
                  </div>
                  <div className="h-36 bg-gradient-to-br from-primary/20 via-secondary/15 to-surface-container-low dark:from-primary/30 dark:to-slate-900 flex items-end p-4 pt-14">
                    <span className="text-on-surface dark:text-white font-headline font-bold text-lg">Day {day.day}</span>
                  </div>
                  <div className="p-5 space-y-4 flex-grow">
                    {result.match_day === day.day && (result.match_day_date || result.match_day_stadium) && (
                      <div className="flex gap-2 items-start text-sm text-tertiary bg-tertiary/10 dark:bg-tertiary/20 px-3 py-2 rounded-xl font-medium">
                        <Clock className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                        <span>
                          Match day
                          {result.match_day_date ? ` · ${result.match_day_date}` : ''}
                          {result.match_day_stadium ? ` · ${result.match_day_stadium}` : ''}
                        </span>
                      </div>
                    )}
                    <SlotSection title="Morning" activities={day.morning} city={itineraryCity} />
                    <SlotSection title="Afternoon" activities={day.afternoon} city={itineraryCity} />
                    <SlotSection title="Evening" activities={day.evening} city={itineraryCity} />
                  </div>
                </article>
              ))}
            </div>

            {(result.top_food_recommendations?.length > 0 ||
              result.football_experiences?.length > 0 ||
              result.travel_tips?.length > 0) && (
              <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-3">
                {result.top_food_recommendations?.length > 0 && (
                  <div className="p-5 rounded-2xl bg-surface-container-low dark:bg-slate-800/60 border border-outline-variant/10">
                    <h3 className="font-headline font-semibold text-on-surface dark:text-white flex items-center gap-2 mb-3">
                      <Utensils className="h-5 w-5 text-secondary" />
                      Top food
                    </h3>
                    <ul className="list-disc list-inside text-sm text-on-surface-variant dark:text-slate-400 space-y-1">
                      {result.top_food_recommendations.map((item, i) => (
                        <li key={i}>{typeof item === 'string' ? item : item?.name ?? item?.description ?? String(item)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.football_experiences?.length > 0 && (
                  <div className="p-5 rounded-2xl bg-surface-container-low dark:bg-slate-800/60 border border-outline-variant/10">
                    <h3 className="font-headline font-semibold text-on-surface dark:text-white flex items-center gap-2 mb-3">
                      <Trophy className="h-5 w-5 text-primary" />
                      Football experiences
                    </h3>
                    <ul className="list-disc list-inside text-sm text-on-surface-variant dark:text-slate-400 space-y-1">
                      {result.football_experiences.map((item, i) => (
                        <li key={i}>{typeof item === 'string' ? item : item?.name ?? item?.description ?? String(item)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.travel_tips?.length > 0 && (
                  <div className="p-5 rounded-2xl bg-surface-container-low dark:bg-slate-800/60 border border-outline-variant/10">
                    <h3 className="font-headline font-semibold text-on-surface dark:text-white flex items-center gap-2 mb-3">
                      <Lightbulb className="h-5 w-5 text-tertiary" />
                      Travel tips
                    </h3>
                    <ul className="list-disc list-inside text-sm text-on-surface-variant dark:text-slate-400 space-y-1">
                      {result.travel_tips.map((item, i) => (
                        <li key={i}>{typeof item === 'string' ? item : item?.name ?? item?.description ?? String(item)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <AffiliateCTA city={itineraryCity} className="mt-4" />
          </section>
        )}
      </main>
    </>
  );
};

export default ItineraryPlanner;
